// src/lib/use-min-price.js — WAGON R5 lot F (prix plancher bloquant)
//
// Hook léger pour gérer `logement_pricing_config.min_price` côté UI (V2) :
//
//   - GET au mount : fetch /api/beds24-mapping?action=pricing_logement_config
//     &logement_id=X → récupère min_price (exposé par PRICING_CONFIG_SELECT_COLS).
//   - save(value) : POST ?action=pricing_set_min_price → UPDATE DB (pas de push
//     Beds24). value NULL/'' accepté (clear → aucun plancher).
//
// Calqué sur use-base-price.js (même mécanique session/fetch/AbortController au
// unmount via mountedRef). min_price n'a PAS d'action « appliquer » (≠ base_price) :
// c'est une borne consommée côté serveur par handlePricingSetManual.

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../supabase";

const LOGEMENT_ID_REGEX = /^[a-z0-9.]+$/i;

/**
 * Hook mono-logement pour le prix plancher (Wagon R5 lot F).
 *
 * @param {string} logementId
 * @returns {{
 *   minPrice: number|null,
 *   loading: boolean,
 *   error: string|null,
 *   refetch: () => Promise<void>,
 *   save: (value: number|null) => Promise<{ok:boolean, error?:string, minPrice?:number|null}>,
 * }}
 */
export function useMinPrice(logementId) {
  const [state, setState] = useState({
    minPrice: null,
    loading: false,
    error: null,
  });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchConfig = useCallback(async () => {
    if (!logementId || !LOGEMENT_ID_REGEX.test(logementId)) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        if (!mountedRef.current) return;
        setState((s) => ({ ...s, loading: false, error: "not_authenticated" }));
        return;
      }
      const qs = new URLSearchParams({
        action: "pricing_logement_config",
        logement_id: logementId,
      });
      const res = await fetch(`/api/beds24-mapping?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        if (!mountedRef.current) return;
        setState((s) => ({
          ...s,
          loading: false,
          error: json.error || `http_${res.status}`,
        }));
        return;
      }
      const json = await res.json();
      if (!mountedRef.current) return;
      const row = (json.configs || []).find(
        (c) => c.logement_id === logementId,
      );
      setState((s) => ({
        ...s,
        minPrice: row?.min_price ?? null,
        loading: false,
        error: null,
      }));
    } catch (e) {
      if (!mountedRef.current) return;
      setState((s) => ({
        ...s,
        loading: false,
        error: e.message || String(e),
      }));
    }
  }, [logementId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const save = useCallback(
    async (newValue) => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) {
          return { ok: false, error: "not_authenticated" };
        }
        const res = await fetch(
          "/api/beds24-mapping?action=pricing_set_min_price",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              logement_id: logementId,
              min_price: newValue,
            }),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          return {
            ok: false,
            status: res.status,
            error: json.error || json.message || `http_${res.status}`,
          };
        }
        if (mountedRef.current) {
          setState((s) => ({ ...s, minPrice: json.min_price ?? null }));
        }
        return { ok: true, minPrice: json.min_price ?? null };
      } catch (e) {
        return { ok: false, error: e.message || String(e) };
      }
    },
    [logementId],
  );

  return {
    minPrice: state.minPrice,
    loading: state.loading,
    error: state.error,
    refetch: fetchConfig,
    save,
  };
}
